import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  CheckRounded,
  CloseFullscreenRounded,
  CloseRounded,
  ContentCopyRounded,
  EditRounded,
  FullscreenRounded,
  HorizontalSplitRounded,
  KeyboardArrowLeftRounded,
  KeyboardArrowRightRounded,
  VerticalSplitRounded,
} from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useChat } from "./ChatContext";

const extractTextFromHtml = (html) => {
  if (typeof html !== "string") {
    return "";
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || "";
};

const buildCopyableMessageText = (message) => {
  if (!message) {
    return "";
  }

  if (message.html_msg) {
    if (typeof message.html_msg === "string") {
      return extractTextFromHtml(message.html_msg);
    }

    if (React.isValidElement(message.html_msg)) {
      return "Formatted message content";
    }
  }

  return (message.answer || message.text || "").trim();
};

const SuggestionChipRail = ({ suggestions = [], disabled = false, onSelect }) => {
  const theme = useTheme();
  const railRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const railNode = railRef.current;

    if (!railNode) {
      return undefined;
    }

    const updateScrollState = () => {
      const maxScrollLeft = railNode.scrollWidth - railNode.clientWidth;
      setCanScrollLeft(railNode.scrollLeft > 4);
      setCanScrollRight(maxScrollLeft - railNode.scrollLeft > 4);
    };

    updateScrollState();
    railNode.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      railNode.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [suggestions]);

  if (!suggestions.length) {
    return null;
  }

  const scrollRail = (direction) => {
    railRef.current?.scrollBy({
      left: direction * 180,
      behavior: "smooth",
    });
  };

  return (
    <Box sx={{ position: "relative", mb: "0.8rem" }}>
      {canScrollLeft && (
        <IconButton
          size="small"
          onClick={() => scrollRail(-1)}
          sx={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            width: 26,
            height: 26,
            bgcolor: alpha(theme.palette.background.paper, 0.96),
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.9),
            boxShadow: "0 4px 10px rgba(15, 23, 42, 0.08)",
          }}
        >
          <KeyboardArrowLeftRounded sx={{ fontSize: "1rem" }} />
        </IconButton>
      )}

      {canScrollRight && (
        <IconButton
          size="small"
          onClick={() => scrollRail(1)}
          sx={{
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            width: 26,
            height: 26,
            bgcolor: alpha(theme.palette.background.paper, 0.96),
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.9),
            boxShadow: "0 4px 10px rgba(15, 23, 42, 0.08)",
          }}
        >
          <KeyboardArrowRightRounded sx={{ fontSize: "1rem" }} />
        </IconButton>
      )}

      <Box
        ref={railRef}
        sx={{
          display: "flex",
          gap: "0.5rem",
          overflowX: "auto",
          scrollBehavior: "smooth",
          pr: canScrollRight ? "2.25rem" : 0,
          pl: canScrollLeft ? "2.25rem" : 0,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {suggestions.map((suggestion) => (
          <Chip
            key={suggestion.id || suggestion.prompt}
            size="small"
            clickable={!disabled}
            label={suggestion.label || suggestion.prompt}
            onClick={() => !disabled && onSelect?.(suggestion)}
            sx={{
              flexShrink: 0,
              height: 28,
              borderRadius: "999px",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.14),
              "& .MuiChip-label": {
                px: 1.2,
                fontSize: "0.72rem",
                fontWeight: 500,
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const QuickLinkRail = ({ links = [], disabled = false, onSelect }) => {
  const theme = useTheme();

  if (!links.length) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.45rem", mb: "0.8rem" }}>
      {links.map((link) => (
        <Chip
          key={link.id || link.route || link.label}
          size="small"
          clickable={!disabled}
          label={link.label}
          onClick={() => !disabled && onSelect?.(link)}
          sx={{
            height: 28,
            borderRadius: "999px",
            bgcolor: alpha(theme.palette.secondary.main, 0.08),
            border: "1px solid",
            borderColor: alpha(theme.palette.secondary.main, 0.16),
            "& .MuiChip-label": {
              px: 1.2,
              fontSize: "0.72rem",
              fontWeight: 500,
            },
          }}
        />
      ))}
    </Box>
  );
};

const MessageBubble = ({
  msg,
  copiedMessageId,
  onCopy,
  onEdit,
  theme,
}) => {
  const isUser = msg.sender === "user";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      style={{
        position: "relative",
        alignSelf: isUser ? "flex-end" : "flex-start",
        maxWidth: "85%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: isUser ? "flex-end" : "flex-start",
          "&:hover .message-actions": {
            opacity: 1,
            transform: "translateY(0)",
          },
          "&:hover .msg_time_span": {
            opacity: 0.8,
            transform: "translateY(0)",
          },
        }}
      >
        <Box
          className="message-actions"
          sx={{
            position: "absolute",
            top: -24,
            [isUser ? "right" : "left"]: 0,
            display: "flex",
            gap: "0.25rem",
            opacity: 0,
            transform: "translateY(4px)",
            transition: "all 0.2s ease",
            bgcolor: "background.paper",
            borderRadius: "0.5rem",
            padding: "0.1rem",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            zIndex: 10,
          }}
        >
          <Tooltip
            title={copiedMessageId === msg.id ? "Copied!" : "Copy"}
            arrow
            placement="top"
          >
            <IconButton
              size="small"
              onClick={() => onCopy(msg, msg.id)}
              sx={{
                width: 26,
                height: 26,
                color:
                  copiedMessageId === msg.id ? "success.main" : "text.secondary",
              }}
            >
              {copiedMessageId === msg.id ? (
                <CheckRounded sx={{ fontSize: "0.9rem" }} />
              ) : (
                <ContentCopyRounded sx={{ fontSize: "0.9rem" }} />
              )}
            </IconButton>
          </Tooltip>

          {isUser && (
            <Tooltip title="Edit" arrow placement="top">
              <IconButton
                size="small"
                onClick={() => onEdit(msg.text)}
                sx={{
                  width: 26,
                  height: 26,
                  color: "text.secondary",
                }}
              >
                <EditRounded sx={{ fontSize: "0.9rem" }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box
          sx={{
            px: "1rem",
            py: "0.75rem",
            borderRadius: isUser
              ? "1.25rem 1.25rem 0.25rem 1.25rem"
              : "1.25rem 1.25rem 1.25rem 0.25rem",
            maxWidth: "100%",
            wordBreak: "break-word",
            background: isUser
              ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 100%)`,
            color: isUser ? "#fff" : "text.primary",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            border: !isUser ? "1px solid" : "none",
            borderColor: "divider",
            cursor: "text",
            fontSize: "0.9rem",
            lineHeight: 1.5,
            "& p, & div, & span": {
              fontSize: "0.9rem",
              lineHeight: 1.5,
              margin: 0,
            },
            "& ol, & ul": {
              margin: "0.25rem 0",
              paddingLeft: "1.25rem",
            },
            "& li": {
              fontSize: "0.9rem",
              lineHeight: 1.5,
              marginBottom: "0.25rem",
            },
          }}
        >
          {msg.html_msg ? (
            typeof msg.html_msg === "string" ? (
              <Box
                sx={{
                  "& p": {
                    margin: "0 0 0.5rem 0",
                    fontSize: "0.9rem",
                    lineHeight: 1.5,
                  },
                  "& p:last-child": { margin: 0 },
                  "& ol, & ul": {
                    margin: "0.25rem 0",
                    paddingLeft: "1.25rem",
                  },
                  "& li": {
                    fontSize: "0.9rem",
                    lineHeight: 1.5,
                    marginBottom: "0.25rem",
                  },
                }}
                dangerouslySetInnerHTML={{ __html: msg.html_msg }}
              />
            ) : (
              <Box>{msg.html_msg}</Box>
            )
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.9rem",
                lineHeight: 1.5,
                color: "inherit",
                whiteSpace: "pre-wrap",
              }}
            >
              {msg.text}
            </Typography>
          )}
        </Box>

        {msg.timestamp && (
          <Typography
            className="msg_time_span"
            variant="caption"
            sx={{
              fontSize: "0.65rem",
              opacity: 0,
              color: "text.secondary",
              transform: "translateY(4px)",
              transition: "all 0.2s ease",
              mt: 0.5,
              mx: 0.5,
            }}
          >
            {new Date(msg.timestamp).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
        )}
      </Box>
    </motion.div>
  );
};

export const ChatBox = ({
  messages = [],
  loading = false,
  onSubmit,
  onClose,
  onLayoutChange,
  currentLayout = "box",
  showLayoutControls = true,
  fullScreen = false,
  style = {},
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { quickLinks, promptSuggestions } = useChat();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const { register, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: { message: "" },
  });
  const messageValue = watch("message");

  useEffect(() => {
    const container = messagesContainerRef.current;

    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setShowScrollButton(scrollHeight - scrollTop - clientHeight >= 100);
    };

    container.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const focusMessageInput = () => {
    const textField = document.querySelector('textarea[name="message"]');
    textField?.focus();
  };

  const handleSubmitMessage = async (data) => {
    if (!data.message?.trim()) {
      focusMessageInput();
      return;
    }

    await onSubmit(data.message);
    reset();
  };

  const handleCopyMessage = (message, messageId) => {
    const textToCopy = buildCopyableMessageText(message);

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 2000);
      })
      .catch(() => {
        const textarea = document.createElement("textarea");
        textarea.value = textToCopy;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 2000);
      });
  };

  const handleEditMessage = (text) => {
    setValue("message", text);
    focusMessageInput();
  };

  const handleSuggestionSelect = (suggestion) => {
    if (!suggestion?.prompt) {
      return;
    }

    setValue("message", suggestion.prompt, {
      shouldDirty: true,
      shouldTouch: true,
    });
    focusMessageInput();
  };

  const handleQuickLinkSelect = (link) => {
    if (link?.route) {
      navigate(link.route);
    }
  };

  const openAiRulesStudio = () => {
    navigate("/dashboard/settings/ai-rules");
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "1rem",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backdropFilter: "blur(10px)",
        bgcolor: "rgba(255, 255, 255, 0.95)",
        border: fullScreen ? "1px solid #e5e5e5" : "none",
        zIndex: 1499,
        ...style,
      }}
    >
      <Box
        sx={{
          p: "0.75rem 1rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e5e5e5",
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08,
          )} 0%, transparent 100%)`,
        }}
      >
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: "primary.main", fontWeight: 600 }}
          >
            LexMom AI
            {fullScreen && " - Full Screen"}
            {currentLayout === "verticalSplit" && " - Vertical Split"}
            {currentLayout === "horizontalSplit" && " - Horizontal Split"}
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 0.45, color: "text.secondary" }}
          >
            General workspace chat with a shortcut to AI Rules Studio.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          <Button
            size="small"
            variant="outlined"
            onClick={openAiRulesStudio}
            sx={{
              mr: 0.5,
              borderRadius: "999px",
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
          >
            AI Rules
          </Button>
          {showLayoutControls && !fullScreen && (
            <>
              <IconButton
                size="small"
                onClick={() => onLayoutChange("horizontalSplit")}
                title="Horizontal Split"
                sx={{
                  p: "4px",
                  color:
                    currentLayout === "horizontalSplit"
                      ? "primary.main"
                      : "text.secondary",
                }}
              >
                <HorizontalSplitRounded sx={{ fontSize: "1rem" }} />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => onLayoutChange("verticalSplit")}
                title="Vertical Split"
                sx={{
                  p: "4px",
                  color:
                    currentLayout === "verticalSplit"
                      ? "primary.main"
                      : "text.secondary",
                }}
              >
                <VerticalSplitRounded sx={{ fontSize: "1rem" }} />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => onLayoutChange("fullScreen")}
                title="Full Screen"
                sx={{
                  p: "4px",
                  color:
                    currentLayout === "fullScreen"
                      ? "primary.main"
                      : "text.secondary",
                }}
              >
                <FullscreenRounded sx={{ fontSize: "1rem" }} />
              </IconButton>
            </>
          )}

          {fullScreen && (
            <IconButton
              size="small"
              onClick={() => onLayoutChange("box")}
              title="Exit Full Screen"
              sx={{ p: "4px", color: "text.secondary" }}
            >
              <CloseFullscreenRounded sx={{ fontSize: "1rem" }} />
            </IconButton>
          )}

          {onClose && (
            <IconButton size="small" onClick={onClose} sx={{ p: "4px" }}>
              <CloseRounded sx={{ fontSize: "1rem", color: "error.main" }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          p: "1rem",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              alignSelf: "center",
              textAlign: "center",
              p: "2rem",
              color: "text.secondary",
              maxWidth: 420,
            }}
          >
            <Typography variant="body2">
              Ask about matters, tasks, documents, bills, or workspace. For
              automation setup, open the AI Rules Studio.
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={openAiRulesStudio}
              sx={{ mt: 1, textTransform: "none" }}
            >
              Open AI Rules Studio
            </Button>
          </Box>
        )}

        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            copiedMessageId={copiedMessageId}
            onCopy={handleCopyMessage}
            onEdit={handleEditMessage}
            theme={theme}
          />
        ))}

        {loading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              px: "1rem",
              py: "0.75rem",
              borderRadius: "1.25rem 1.25rem 1.25rem 0.25rem",
              maxWidth: "fit-content",
              bgcolor: alpha(theme.palette.background.paper, 0.8),
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CircularProgress size={16} thickness={5} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Thinking...
            </Typography>
          </Box>
        )}

        {showScrollButton && (
          <IconButton
            onClick={() =>
              messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            sx={{
              position: "sticky",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              bgcolor: "primary.main",
              color: "white",
              width: 32,
              height: 32,
              zIndex: 10,
              boxShadow: 3,
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            <ArrowDownwardRounded sx={{ fontSize: "1.1rem" }} />
          </IconButton>
        )}

        <div ref={messagesEndRef} />
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit(handleSubmitMessage)}
        sx={{
          p: "1rem",
          borderTop: "1px solid #e5e5e5",
          bgcolor: "background.paper",
        }}
      >
        <SuggestionChipRail
          suggestions={promptSuggestions}
          disabled={loading}
          onSelect={handleSuggestionSelect}
        />

        <QuickLinkRail
          links={quickLinks}
          disabled={loading}
          onSelect={handleQuickLinkSelect}
        />

        <TextField
          placeholder="Ask about matters, tasks, documents, bills, or workspace..."
          fullWidth
          multiline
          minRows={1}
          maxRows={fullScreen ? 6 : 4}
          disabled={loading}
          variant="outlined"
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              handleSubmit(handleSubmitMessage)();
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              p: "0rem",
              borderRadius: "1.5rem",
              bgcolor: alpha(theme.palette.background.default, 0.5),
              "&:hover": {
                bgcolor: alpha(theme.palette.background.default, 0.8),
              },
              "&.Mui-focused": {
                bgcolor: "background.paper",
                "& fieldset": {
                  borderColor: "primary.main",
                  borderWidth: "2px",
                },
              },
              "& textarea": {
                resize: "none",
                overflow: "auto",
                lineHeight: 1.5,
                fontSize: "0.9rem",
                padding: "0.75rem 1rem",
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <IconButton
                type="submit"
                disabled={!messageValue?.trim() || loading}
                sx={{
                  alignSelf: "flex-end",
                  width: 36,
                  height: 36,
                  mr: 0.5,
                  mb: 0.5,
                  bgcolor: "primary.main",
                  color: "#fff",
                  "&:hover": { bgcolor: "primary.dark" },
                  "&.Mui-disabled": {
                    bgcolor: "action.disabledBackground",
                    color: "action.disabled",
                  },
                }}
              >
                <ArrowUpwardRounded sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            ),
          }}
          {...register("message")}
        />
      </Box>
    </Box>
  );
};

const ChatBotFabAnimated = ({
  chatBotView,
  setChatBotView,
  chatOpen,
  setChatOpen,
}) => {
  const { messages, loading, sendMessage } = useChat();
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [corner, setCorner] = useState("bottom-right");
  const theme = useTheme();

  useEffect(() => {
    if (chatBotView === "box") {
      const buttonSize = 60;
      setPosition({
        x: window.innerWidth - buttonSize - 20,
        y: window.innerHeight - buttonSize - 20,
      });
    }
  }, [chatBotView]);

  const toggleChat = () => {
    if (!chatOpen) {
      setChatBotView("box");
    }
    setChatOpen(!chatOpen);
  };

  const handleDragEnd = (event, info) => {
    const { x, y } = info.point;
    const width = window.innerWidth;
    const height = window.innerHeight;
    const threshold = 50;
    const buttonSize = 60;
    let newCorner = corner;
    let newX = x;
    let newY = y;

    if (x < threshold) {
      newX = 20;
      if (y < threshold) {
        newY = 20;
        newCorner = "top-left";
      } else if (y > height - threshold - buttonSize) {
        newY = height - buttonSize - 20;
        newCorner = "bottom-left";
      } else {
        newCorner = "left";
      }
    } else if (x > width - threshold - buttonSize) {
      newX = width - buttonSize - 20;
      if (y < threshold) {
        newY = 20;
        newCorner = "top-right";
      } else if (y > height - threshold - buttonSize) {
        newY = height - buttonSize - 20;
        newCorner = "bottom-right";
      } else {
        newCorner = "right";
      }
    } else if (y < threshold) {
      newY = 20;
      newCorner = "top";
    } else if (y > height - threshold - buttonSize) {
      newY = height - buttonSize - 20;
      newCorner = "bottom";
    }

    setPosition({ x: newX, y: newY });
    setCorner(newCorner);
    setIsDragging(false);
  };

  const getBorderRadius = () => {
    switch (corner) {
      case "top-left":
        return "0 100px 100px 100px";
      case "top-right":
        return "100px 0 100px 100px";
      case "bottom-left":
        return "100px 100px 100px 0";
      case "bottom-right":
        return "100px 100px 0 100px";
      case "left":
        return "100px 100px 100px 0";
      case "right":
        return "0 100px 100px 100px";
      default:
        return "100px";
    }
  };

  const getChatWindowPosition = () => {
    const buttonSize = 60;

    switch (corner) {
      case "top-left":
        return { top: buttonSize + 20, left: 20 };
      case "top-right":
        return { top: buttonSize + 20, right: 20 };
      case "bottom-left":
        return { bottom: buttonSize + 20, left: 20 };
      case "bottom-right":
        return { bottom: buttonSize + 20, right: 20 };
      case "left":
        return {
          top: Math.max(
            20,
            Math.min(position.y - 190, window.innerHeight - 440),
          ),
          left: buttonSize + 20,
        };
      case "right":
        return {
          top: Math.max(
            20,
            Math.min(position.y - 190, window.innerHeight - 440),
          ),
          right: buttonSize + 20,
        };
      default:
        return { bottom: buttonSize + 20, right: 20 };
    }
  };

  if (chatBotView !== "box" && chatOpen) {
    return null;
  }

  return (
    <>
      {chatOpen && chatBotView === "box" && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
          drag
          dragMomentum={false}
          dragConstraints={{
            top: 20,
            left: 20,
            right: window.innerWidth - 380,
            bottom: window.innerHeight - 480,
          }}
          style={{
            position: "fixed",
            ...getChatWindowPosition(),
            zIndex: 1500,
            width: 360,
            height: 480,
          }}
        >
          <ChatBox
            messages={messages}
            loading={loading}
            onSubmit={sendMessage}
            onLayoutChange={setChatBotView}
            currentLayout={chatBotView}
            onClose={() => setChatOpen(false)}
          />
        </motion.div>
      )}

      <motion.div
        initial={false}
        animate={{
          scale: chatOpen ? 1.1 : 1,
          x: position.x,
          y: position.y,
        }}
        transition={{ duration: 0.3, type: "spring", stiffness: 200 }}
        drag
        dragMomentum={false}
        dragConstraints={{
          top: 0,
          left: 0,
          right: window.innerWidth - 60,
          bottom: window.innerHeight - 60,
        }}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          zIndex: 1500,
          cursor: isDragging ? "grabbing" : "grab",
          touchAction: "none",
          display: chatBotView === "box" ? "block" : "none",
        }}
      >
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Box
            onClick={toggleChat}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              boxShadow: "5px 5px 10px rgba(0, 0, 0, 0.24)",
              width: 60,
              height: 60,
              borderRadius: getBorderRadius(),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
              <Box
                sx={{
                  position: "absolute",
                  width: 8,
                  height: 8,
                  backgroundColor: "#fff",
                  borderRadius: "100px",
                  top: "50%",
                  left: "12px",
                  transform: "translateY(-50%)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  width: 8,
                  height: 8,
                  backgroundColor: "#fff",
                  borderRadius: "100px",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  width: 8,
                  height: 8,
                  backgroundColor: "#fff",
                  borderRadius: "100px",
                  top: "50%",
                  right: "12px",
                  transform: "translateY(-50%)",
                }}
              />
            </Box>
          </Box>
        </motion.div>
      </motion.div>
    </>
  );
};

export default ChatBotFabAnimated;
